import React, { useState, useEffect } from "react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { LifeBuoy, Loader2, Mail, Phone, Clock } from "lucide-react";

export default function SupportContact() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/platform/contact`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setData(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  }

  const name = data?.platform_name || "Site Pro App";
  const email = data?.support_email;
  const sla = data?.callback_sla || "24 hours";
  const logo = data?.logo_url ? `${API.replace(/\/api$/, "")}${data.logo_url}` : null;
  const mailto = email
    ? `mailto:${email}?subject=${encodeURIComponent("Support request — " + name)}&body=${encodeURIComponent("Please describe your issue:\n\n\nYour name:\nBest contact number:\nCompany/site:\n")}`
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="support-contact-page">
      <div className="flex items-center gap-3 mb-6">
        {logo ? (
          <img src={logo} alt={name} className="w-11 h-11 object-contain" />
        ) : (
          <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center"><LifeBuoy className="w-6 h-6 text-slate-900" /></div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contact Support</h1>
          <p className="text-slate-500 text-sm">Help with {name}</p>
        </div>
      </div>

      <Card className="border-amber-100 shadow-sm">
        <CardContent className="p-6">
          {data?.full ? (
            <div className="space-y-4" data-testid="support-full">
              <p className="text-slate-600">Reach the {name} team directly:</p>
              {email && (
                <div className="flex items-center gap-3 text-slate-800" data-testid="support-email-row">
                  <Mail className="w-5 h-5 text-amber-500" />
                  <a href={mailto} className="font-medium text-amber-700 hover:underline">{email}</a>
                </div>
              )}
              {data?.support_phone && (
                <div className="flex items-center gap-3 text-slate-800" data-testid="support-phone-row">
                  <Phone className="w-5 h-5 text-amber-500" />
                  <span className="font-medium">{data.support_phone}</span>
                </div>
              )}
              {data?.support_hours && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span>{data.support_hours}</span>
                </div>
              )}
              {mailto && (
                <Button asChild className="mt-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="support-email-btn">
                  <a href={mailto}>Email us</a>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4 text-center" data-testid="support-lite">
              <LifeBuoy className="w-10 h-10 mx-auto text-amber-400" />
              <p className="text-slate-700 text-base leading-relaxed">
                {data?.message ||
                  `Need support? Send us an email with your issue and your contact details, and someone will call you back within ${sla}.`}
              </p>
              {mailto && (
                <Button asChild className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="support-email-btn">
                  <a href={mailto}><Mail className="w-4 h-4 mr-2" /> Email support</a>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
